/*
  Custom JS for House of Zena
  Wrapped in IIFE to avoid global variable collisions
*/
(function() {
  'use strict';

  /* ============================================================
     1. Pragma / Shiprocket / Pickrr Widget Removal (Safe & Throttled)
     ============================================================ */
  // Only the floating marketing widgets are removed here, addressed by their own
  // ids. The previous version matched on [class*="shiprocket"], [class*="fastrr"]
  // and [class*="pickrr"], which also matches Shiprocket's Fastrr checkout - the
  // button and modal the store actually takes payment through. That never
  // surfaced because a syntax error further down this file stopped it running;
  // now that the file parses, a broad match here would delete the checkout.
  const WIDGET_IDS = [
    'sr-whatsapp-widget',
    'pickrr-whatsapp-widget',
    'pragma-widget',
    'pragma-tracking-widget'
  ];

  // Anything inside these is part of the purchase path and is never touched.
  const CHECKOUT_SAFE_ZONES = [
    '#offcanvas-cart',
    '#header-group',
    '#navbar-wrapper',
    '[class*="sr-headless"]',
    '[class*="sr-checkout"]',
    '[id*="checkout"]'
  ].join(',');

  const removeThirdPartyWidgets = () => {
    WIDGET_IDS.forEach(id => {
      const el = document.getElementById(id);
      if (!el || el.closest(CHECKOUT_SAFE_ZONES)) return;
      if (el.querySelector('[class*="sr-headless"], [class*="sr-checkout"], button[name="checkout"]')) return;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      try { el.remove(); } catch (e) {}
    });
  };

  removeThirdPartyWidgets();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeThirdPartyWidgets, { once: true, passive: true });
  }
  window.addEventListener('load', removeThirdPartyWidgets, { once: true, passive: true });

  let isRemoving = false;
  let widgetObserver = null;
  if (typeof MutationObserver !== 'undefined') {
    widgetObserver = new MutationObserver(() => {
      if (isRemoving) return;
      isRemoving = true;
      setTimeout(() => {
        removeThirdPartyWidgets();
        isRemoving = false;
      }, 150);
    });

    const observeTarget = document.body || document.documentElement;
    if (observeTarget) {
      widgetObserver.observe(observeTarget, { childList: true });
    }
  }

  setTimeout(() => {
    if (widgetObserver) widgetObserver.disconnect();
  }, 2500);

  /* ============================================================
     2. House of Zena World-Class Predictive Search Subsystem
     ============================================================ */
  const initExpandableSearch = () => {
    const escapeHtml = (str) => {
      if (!str) return '';
      return String(str).replace(/[&<>"']/g, (m) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      })[m]);
    };

    const formatPrice = (centsOrNumber) => {
      if (!centsOrNumber) return '';
      const num = parseFloat(centsOrNumber);
      if (isNaN(num)) return centsOrNumber;
      return '₹' + Math.round(num).toLocaleString('en-IN');
    };

    const highlightMatches = (text, query) => {
      if (!query || !text) return escapeHtml(text);
      const tokens = query.trim().split(/\s+/).filter(t => t.length > 0);
      if (!tokens.length) return escapeHtml(text);
      
      const pattern = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
      const regex = new RegExp(`(${pattern})`, 'gi');
      return escapeHtml(text).replace(regex, '<mark class="zena-search-highlight">$1</mark>');
    };

    // Synonym dictionary for semantic search expansion
    const SEARCH_SYNONYMS = {
      'ring': ['band', 'signet'],
      'band': ['ring'],
      'necklace': ['chain', 'pendant', 'choker'],
      'chain': ['necklace', 'link'],
      'earring': ['hoops', 'studs', 'drops', 'huggies', 'huggie'],
      'earrings': ['hoops', 'studs', 'drops', 'huggies'],
      'hoops': ['earring', 'hoop', 'earrings'],
      'studs': ['earring', 'stud', 'earrings'],
      'bracelet': ['bangle', 'cuff', 'brace'],
      'bangle': ['bracelet'],
      'anklet': ['ankle', 'anklets'],
      'gold': ['plated', 'golden'],
      'silver': ['steel', 'platinum'],
      'tennis': ['bracelet', 'chain'],
      'waterproof': ['tarnish', 'anti-tarnish'],
      'gift': ['gifting', 'present'],
      'luxury': ['premium', 'designer'],
      'croissant': ['butter', 'hoops'],
      'pavé': ['pave', 'crystal', 'diamond'],
      'pave': ['pavé', 'crystal']
    };

    // Levenshtein distance for fuzzy matching (typo tolerance)
    const levenshteinDistance = (a, b) => {
      if (!a || !b) return Math.max((a || '').length, (b || '').length);
      if (a === b) return 0;
      const aLen = a.length, bLen = b.length;
      if (aLen === 0) return bLen;
      if (bLen === 0) return aLen;
      // Use single-row optimization for memory efficiency
      let prev = Array.from({ length: bLen + 1 }, (_, i) => i);
      for (let i = 1; i <= aLen; i++) {
        let curr = [i];
        for (let j = 1; j <= bLen; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        }
        prev = curr;
      }
      return prev[bLen];
    };

    // URL sanitizer to prevent javascript: protocol injection
    const sanitizeUrl = (url) => {
      if (!url) return '#';
      const clean = String(url).trim();
      // "//host" is protocol-relative and would leave the store, so only
      // same-origin paths and absolute http(s) URLs are allowed through.
      if (clean.startsWith('//')) return '#';
      if (clean.startsWith('/') || clean.startsWith('https://') || clean.startsWith('http://')) return clean;
      return '#';
    };

    // Cached search index (built once, reused on every keystroke)
    let _cachedSearchIndex = null;
    let _cachedSearchIndexSize = -1;

    // Client-side in-memory search index built dynamically from window.zenaTitleMap & handleMap
    const getSearchIndex = () => {
      const titleMap = window.zenaTitleMap || {};
      const handleMap = window.zenaHandleMap || {};
      
      // Return cached index if titleMap hasn't changed
      const currentSize = Object.keys(titleMap).length;
      if (_cachedSearchIndex && _cachedSearchIndexSize === currentSize) {
        return _cachedSearchIndex;
      }

      const catalog = [];
      const handleSet = new Set();

      for (const [origTitle, relatableTitle] of Object.entries(titleMap)) {
        const cleanOrig = origTitle.replace(/\s+/g, ' ').trim();
        let handle = '';
        for (const [h, rt] of Object.entries(handleMap)) {
          if (rt === relatableTitle) {
            handle = h;
            break;
          }
        }
        if (!handle) {
          handle = cleanOrig.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        }

        if (!handleSet.has(handle)) {
          handleSet.add(handle);
          catalog.push({
            relatableTitle: relatableTitle,
            originalTitle: cleanOrig,
            handle: handle,
            url: `/products/${handle}`,
            searchString: `${relatableTitle} ${cleanOrig} ${handle.replace(/-/g, ' ')}`.toLowerCase(),
            // Pre-split words for fuzzy matching
            searchWords: `${relatableTitle} ${cleanOrig} ${handle.replace(/-/g, ' ')}`.toLowerCase().split(/\s+/)
          });
        }
      }
      
      // Cache the built index
      _cachedSearchIndex = catalog;
      _cachedSearchIndexSize = currentSize;
      return catalog;
    };

    // Fast multi-token scoring engine with fuzzy matching & synonym expansion
    const searchLocalCatalog = (query) => {
      if (!query || !query.trim()) return [];
      const qClean = query.trim().toLowerCase();
      const qTokens = qClean.split(/\s+/).filter(t => t.length > 0);
      const catalog = getSearchIndex();
      const scored = [];

      // Expand query tokens with synonyms for semantic matching
      const synonymTokens = new Set();
      for (const t of qTokens) {
        const syns = SEARCH_SYNONYMS[t];
        if (syns) {
          syns.forEach(s => synonymTokens.add(s));
        }
      }

      for (const item of catalog) {
        const rLower = item.relatableTitle.toLowerCase();
        const oLower = item.originalTitle.toLowerCase();
        let score = 0;

        // Exact & partial full-query matching (highest priority)
        if (rLower === qClean) {
          score += 150;
        } else if (rLower.startsWith(qClean)) {
          score += 100;
        } else if (rLower.includes(qClean)) {
          score += 75;
        }

        if (oLower === qClean) {
          score += 60;
        } else if (oLower.includes(qClean)) {
          score += 40;
        }

        // Direct token matching
        let tokenMatches = 0;
        for (const t of qTokens) {
          if (item.searchString.includes(t)) {
            tokenMatches++;
            score += 15;
          }
        }

        // Synonym matching (lower weight than direct)
        for (const syn of synonymTokens) {
          if (item.searchString.includes(syn)) {
            score += 8;
          }
        }

        // All-tokens-matched bonus
        if (tokenMatches === qTokens.length && qTokens.length > 1) {
          score += 30;
        }

        // Fuzzy matching for typo tolerance (only for tokens >= 4 chars to avoid false positives)
        if (score === 0 && item.searchWords) {
          for (const t of qTokens) {
            if (t.length >= 4) {
              for (const word of item.searchWords) {
                if (word.length >= 4) {
                  const dist = levenshteinDistance(t, word);
                  if (dist <= 1) {
                    score += 12; // Very close match
                    break;
                  } else if (dist === 2 && t.length >= 5) {
                    score += 6; // Reasonable match for longer words
                    break;
                  }
                }
              }
            }
          }
        }

        if (score > 0) {
          scored.push({
            ...item,
            score: score
          });
        }
      }

      scored.sort((a, b) => b.score - a.score);
      return scored;
    };

    // Search analytics: track query → click pairs in sessionStorage
    const trackSearchClick = (query, productUrl) => {
      try {
        const key = 'zena-search-analytics';
        const data = JSON.parse(sessionStorage.getItem(key) || '[]');
        data.push({ q: query, url: productUrl, ts: Date.now() });
        if (data.length > 50) data.shift();
        sessionStorage.setItem(key, JSON.stringify(data));
      } catch (e) {}
    };

    // Recently searched: persist last 5 terms
    const getRecentSearches = () => {
      try {
        return JSON.parse(localStorage.getItem('zena-recent-searches') || '[]').slice(0, 5);
      } catch (e) { return []; }
    };
    const saveRecentSearch = (term) => {
      try {
        if (!term || term.length < 2) return;
        let recent = getRecentSearches();
        recent = recent.filter(t => t !== term);
        recent.unshift(term);
        if (recent.length > 5) recent = recent.slice(0, 5);
        localStorage.setItem('zena-recent-searches', JSON.stringify(recent));
      } catch (e) {}
    };

    const renderInitialState = (dropdown, input) => {
      if (!dropdown) return;
      
      // Build recently searched chips if available
      const recentSearches = getRecentSearches();
      let recentHtml = '';
      if (recentSearches.length > 0) {
        const chips = recentSearches.map(term => `
          <button type="button" class="zena-search-tag-chip" data-search-term="${escapeHtml(term)}">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            ${escapeHtml(term)}
          </button>
        `).join('');
        recentHtml = `
          <div class="zena-search-section">
            <div class="zena-search-section-header">
              <span>RECENTLY SEARCHED</span>
            </div>
            <div class="zena-search-tags-wrap">
              ${chips}
            </div>
          </div>
        `;
      }

      dropdown.innerHTML = `
        <div class="zena-search-content-inner">
          ${recentHtml}
          <div class="zena-search-section">
            <div class="zena-search-section-header">
              <span>TRENDING PIECES &amp; KEYWORDS</span>
            </div>
            <div class="zena-search-tags-wrap">
              <button type="button" class="zena-search-tag-chip" data-search-term="Croissant">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                The Butter Croissant Hoops
              </button>
              <button type="button" class="zena-search-tag-chip" data-search-term="Rich Auntie">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                The "Rich Auntie" Pavé Chain
              </button>
              <button type="button" class="zena-search-tag-chip" data-search-term="Tennis">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                Tennis Bracelets
              </button>
            </div>
          </div>
          <div class="zena-search-section mt-3 pt-2 border-top">
            <div class="zena-search-section-header">
              <span>EXPLORE COLLECTIONS</span>
            </div>
            <div class="zena-search-categories-grid">
              <a href="/collections/bestsellers" class="zena-search-cat-link">Best Sellers →</a>
              <a href="/collections/new-arrivals" class="zena-search-cat-link">New In →</a>
              <a href="/collections/necklaces" class="zena-search-cat-link">Necklaces →</a>
              <a href="/collections/bracelets" class="zena-search-cat-link">Bracelets →</a>
              <a href="/collections/earrings" class="zena-search-cat-link">Earrings →</a>
              <a href="/collections/rings" class="zena-search-cat-link">Rings →</a>
            </div>
          </div>
        </div>
      `;
      dropdown.classList.add('is-visible');
    };

    window.renderZenaSearchInitialState = renderInitialState;

    let abortControllers = new WeakMap();

    // Global Delegated Click Handler (Capture Phase): Guarantees 100% search clickability even after DOM updates
    if (!window.zenaSearchGlobalBound) {
      window.zenaSearchGlobalBound = true;
      document.addEventListener('click', (e) => {
        const triggerBtn = e.target.closest('.expand-search-trigger');
        if (triggerBtn) {
          e.preventDefault();
          e.stopPropagation();

          const box = triggerBtn.closest('.expand-left-search-box');
          if (box) {
            const isOpen = box.classList.contains('is-open');
            document.querySelectorAll('.expand-left-search-box.is-open').forEach(b => {
              if (b !== box) {
                b.classList.remove('is-open');
                const d = b.querySelector('.zena-live-search-dropdown');
                if (d) d.classList.remove('is-visible');
              }
            });

            if (isOpen) {
              box.classList.remove('is-open');
              const d = box.querySelector('.zena-live-search-dropdown');
              if (d) d.classList.remove('is-visible');
            } else {
              box.classList.add('is-open');
              const input = box.querySelector('.expand-search-input');
              const dropdown = box.querySelector('.zena-live-search-dropdown');
              if (input) {
                setTimeout(() => {
                  input.focus();
                  if (!input.value.trim() && dropdown) {
                    renderInitialState(dropdown, input);
                  }
                }, 80);
              }
            }
          }
          return;
        }

        const closeBtn = e.target.closest('.expand-search-close');
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const box = closeBtn.closest('.expand-left-search-box');
          if (box) {
            box.classList.remove('is-open');
            const dropdown = box.querySelector('.zena-live-search-dropdown');
            if (dropdown) dropdown.classList.remove('is-visible');
            const input = box.querySelector('.expand-search-input');
            if (input) input.value = '';
          }
          return;
        }
      }, true);
    }

    // Bind on each expandable search box
    document.querySelectorAll('.expand-left-search-box').forEach(box => {
      const input = box.querySelector('.expand-search-input');
      const dropdown = box.querySelector('.zena-live-search-dropdown');
      const spinner = box.querySelector('.expand-search-spinner');

      if (input && !input.dataset.searchBound) {
        input.dataset.searchBound = 'true';

        // Focus event: show initial suggestions if empty
        input.addEventListener('focus', () => {
          if (!input.value.trim() && dropdown) {
            renderInitialState(dropdown, input);
          }
        });

        // Hybrid Intelligent Predictive Search Engine
        let debounceTimer = null;
        input.addEventListener('input', () => {
          const query = input.value.trim();

          if (!query) {
            if (spinner) spinner.classList.add('d-none');
            if (dropdown) renderInitialState(dropdown, input);
            return;
          }

          if (spinner) spinner.classList.remove('d-none');
          clearTimeout(debounceTimer);

          // Adaptive debounce: shorter for longer queries (user knows what they want)
          const debounceMs = query.length < 3 ? 220 : 120;
          debounceTimer = setTimeout(async () => {
            let controller = abortControllers.get(input);
            if (controller) controller.abort();
            controller = new AbortController();
            abortControllers.set(input, controller);

            // Step 1: Compute immediate in-memory matches from local 182-item catalog index
            const localMatches = searchLocalCatalog(query);

            // Step 2: Formulate translated queries for Shopify search API
            const remoteQueries = [query];
            if (localMatches.length > 0) {
              // Add original titles of top matching items so Shopify returns rich metadata
              localMatches.slice(0, 3).forEach(lm => {
                if (lm.originalTitle && !remoteQueries.includes(lm.originalTitle)) {
                  remoteQueries.push(lm.originalTitle);
                }
              });
            }

            try {
              // Fetch primary query from Shopify suggest API
              const primaryUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[options][fields]=title,product_type,variants.title,vendor,tag&resources[options][unavailable_products]=last&resources[limit]=10`;
              
              const fetchPromises = [fetch(primaryUrl, { signal: controller.signal })];

              // If top local match has distinct original title, fetch it in parallel
              if (localMatches.length > 0 && localMatches[0].originalTitle && localMatches[0].originalTitle.toLowerCase() !== query.toLowerCase()) {
                const altUrl = `/search/suggest.json?q=${encodeURIComponent(localMatches[0].originalTitle)}&resources[type]=product&resources[limit]=6`;
                fetchPromises.push(fetch(altUrl, { signal: controller.signal }).catch(() => null));
              }

              const responses = await Promise.all(fetchPromises);
              if (spinner) spinner.classList.add('d-none');

              let combinedProducts = [];
              let combinedCollections = [];
              let combinedQueries = [];
              const seenProductHandles = new Set();

              // Process primary response
              if (responses[0] && responses[0].ok) {
                const data = await responses[0].json();
                const res = data.resources?.results || {};
                
                (res.products || []).forEach(p => {
                  const h = p.handle || (p.url ? p.url.split('/products/').pop()?.split('?')[0] : '');
                  if (h && !seenProductHandles.has(h)) {
                    seenProductHandles.add(h);
                    combinedProducts.push(p);
                  }
                });

                combinedCollections = res.collections || [];
                combinedQueries = res.queries || [];
              }

              // Process secondary response
              if (responses[1] && responses[1].ok) {
                const altData = await responses[1].json();
                const altRes = altData.resources?.results || {};
                (altRes.products || []).forEach(p => {
                  const h = p.handle || (p.url ? p.url.split('/products/').pop()?.split('?')[0] : '');
                  if (h && !seenProductHandles.has(h)) {
                    seenProductHandles.add(h);
                    combinedProducts.push(p);
                  }
                });
              }

              // Merge local index matches if not yet in results
              localMatches.forEach(lm => {
                if (!seenProductHandles.has(lm.handle)) {
                  seenProductHandles.add(lm.handle);
                  combinedProducts.push({
                    title: lm.relatableTitle,
                    handle: lm.handle,
                    url: lm.url,
                    image: '',
                    price: '',
                    compare_at_price_max: ''
                  });
                }
              });

              // Add matching relatable titles into suggestion queries
              if (localMatches.length > 0 && combinedQueries.length < 4) {
                localMatches.slice(0, 2).forEach(lm => {
                  if (!combinedQueries.some(q => q.text === lm.relatableTitle)) {
                    combinedQueries.unshift({
                      text: lm.relatableTitle,
                      url: lm.url
                    });
                  }
                });
              }

              // Render finalized hybrid results
              const finalResults = {
                products: combinedProducts.slice(0, 8),
                collections: combinedCollections,
                queries: combinedQueries.slice(0, 3)
              };

              renderResults(dropdown, finalResults, query);
            } catch (err) {
              if (err.name !== 'AbortError') {
                if (spinner) spinner.classList.add('d-none');
                // Even on network error, render immediate local matches!
                if (localMatches.length > 0) {
                  renderResults(dropdown, {
                    products: localMatches.slice(0, 6).map(lm => ({
                      title: lm.relatableTitle,
                      handle: lm.handle,
                      url: lm.url
                    })),
                    collections: [],
                    queries: []
                  }, query);
                } else {
                  renderNoResults(dropdown, query);
                }
              }
            }
          }, debounceMs);
        });

        // Keyboard navigation (ArrowDown, ArrowUp, Enter, Escape)
        input.addEventListener('keydown', (e) => {
          if (!dropdown || !dropdown.classList.contains('is-visible')) return;

          const navigableItems = Array.from(dropdown.querySelectorAll('.zena-search-product-item, .zena-search-tag-chip, .zena-search-cat-link, .zena-search-query-item, .zena-search-view-all-btn'));
          if (!navigableItems.length) return;

          let currentIndex = navigableItems.findIndex(el => el.classList.contains('is-active-item'));

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (currentIndex >= 0) navigableItems[currentIndex].classList.remove('is-active-item');
            currentIndex = (currentIndex + 1) % navigableItems.length;
            navigableItems[currentIndex].classList.add('is-active-item');
            navigableItems[currentIndex].scrollIntoView({ block: 'nearest' });
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (currentIndex >= 0) navigableItems[currentIndex].classList.remove('is-active-item');
            currentIndex = (currentIndex - 1 + navigableItems.length) % navigableItems.length;
            navigableItems[currentIndex].classList.add('is-active-item');
            navigableItems[currentIndex].scrollIntoView({ block: 'nearest' });
          } else if (e.key === 'Enter') {
            if (currentIndex >= 0 && navigableItems[currentIndex]) {
              e.preventDefault();
              navigableItems[currentIndex].click();
            }
          } else if (e.key === 'Escape') {
            box.classList.remove('is-open');
            dropdown.classList.remove('is-visible');
            input.blur();
          }
        });
      }
    });

    // Close all on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.expand-left-search-box')) {
        document.querySelectorAll('.expand-left-search-box.is-open').forEach(b => {
          b.classList.remove('is-open');
          const d = b.querySelector('.zena-live-search-dropdown');
          if (d) d.classList.remove('is-visible');
        });
      }
    });

    // Close on Escape global
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.expand-left-search-box.is-open').forEach(b => {
          b.classList.remove('is-open');
          const d = b.querySelector('.zena-live-search-dropdown');
          if (d) d.classList.remove('is-visible');
        });
      }
    });

    // Track search result clicks for analytics & recently searched
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('.zena-search-product-item');
      const queryLink = e.target.closest('.zena-search-query-item');
      const tagChip = e.target.closest('.zena-search-tag-chip');
      
      if (productLink || queryLink || tagChip) {
        const searchBox = e.target.closest('.expand-left-search-box');
        const input = searchBox?.querySelector('input[type="search"], input[type="text"]');
        const currentQuery = input?.value?.trim();
        
        if (currentQuery && currentQuery.length >= 2) {
          saveRecentSearch(currentQuery);
          if (productLink) {
            trackSearchClick(currentQuery, productLink.getAttribute('href'));
          }
        }
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExpandableSearch);
  } else {
    initExpandableSearch();
  }

/* ============================================================
   3. Zena % Deals Copy & Toast Notification
   ============================================================ */
window.copyCouponCode = function(code, element) {
  if (!code) return;
  const rawCode = String(code).trim().toUpperCase();
  // Sanitize coupon code for safe HTML insertion
  const _esc = (s) => String(s || '').replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'})[m]);
  const safeCode = _esc(rawCode);

  // 1. Copy to clipboard
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(rawCode).catch(() => {});
  } else {
    try {
      const tempInput = document.createElement('input');
      tempInput.value = rawCode;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
    } catch (e) {}
  }

  // 2. Tactile feedback
  try {
    if (navigator.vibrate) navigator.vibrate([30]);
  } catch (e) {}

  // 3. Persist active coupon in localStorage and activate on Shopify session
  try {
    localStorage.setItem('zena_active_coupon', rawCode);
    fetch(`/discount/${encodeURIComponent(rawCode)}`, { redirect: 'follow' }).catch(() => {});
  } catch (e) {}

  // 4. Auto-populate cart discount input if present
  const discountInput = document.querySelector('cart-discount input');
  if (discountInput) {
    discountInput.value = rawCode;
  }

  // 5. Visual transformation on clicked button / pill
  if (element) {
    element.style.transform = 'scale(0.95)';
    setTimeout(() => { element.style.transform = 'scale(1)'; }, 150);

    const copyBtn = element.querySelector('.deal-copy-btn, .offer-copy-btn, .btn, span');
    if (copyBtn && !element.dataset.origHtml) {
      element.dataset.origHtml = copyBtn.innerHTML;
      copyBtn.innerHTML = 'COPIED! ✓';
      copyBtn.style.color = '#22C55E';
      copyBtn.style.fontWeight = '700';

      setTimeout(() => {
        if (element.dataset.origHtml) {
          copyBtn.innerHTML = element.dataset.origHtml;
          copyBtn.style.color = '';
          copyBtn.style.fontWeight = '';
          delete element.dataset.origHtml;
        }
      }, 2000);
    }
  }

  // 6. Create & show luxury top popup notification
  let toast = document.querySelector('.coupon-copied-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'coupon-copied-toast';
    document.body.appendChild(toast);
  }

  toast.innerHTML = `
    <div style="width: 22px; height: 22px; background: #22C55E; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    </div>
    <span>Code <strong style="color: #F8D794; font-family: monospace; font-size: 14px; letter-spacing: 0.05em;">${safeCode}</strong> copied &amp; applied!</span>
  `;
  
  toast.classList.remove('show');
  void toast.offsetWidth; // Trigger reflow for instant re-animation
  toast.classList.add('show');
  
  if (window.couponToastTimeout) clearTimeout(window.couponToastTimeout);
  window.couponToastTimeout = setTimeout(() => {
    toast.classList.remove('show');
  }, 2800);

  // Backup Toast
  if (typeof window.showZenaToast === 'function') {
    window.showZenaToast('Promo Code Copied!', `Code ${safeCode} copied to clipboard and applied.`);
  }
};

/* ============================================================
   4. Pincode Delivery Estimator
   ============================================================ */
window.checkPincodeDelivery = function() {
  const input = document.getElementById('pdp-pincode-input');
  const resultBox = document.getElementById('pdp-pincode-result');
  const btn = document.getElementById('pdp-pincode-btn');
  
  if (!input || !resultBox) return;
  
  const val = input.value.trim();
  if (!/^\d{6}$/.test(val)) {
    resultBox.style.display = 'block';
    resultBox.innerHTML = `
      <div class="text-danger d-flex align-items-center gap-1">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
        Please enter a valid 6-digit Pincode.
      </div>
    `;
    return;
  }

  // Calculate ETA: 3-5 business days from current date
  const etaDate = new Date();
  etaDate.setDate(etaDate.getDate() + 4);
  const dateFormatted = etaDate.toLocaleDateString('en-IN', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });

  resultBox.style.display = 'block';
  resultBox.innerHTML = `
    <div class="d-flex align-items-center gap-2 text-success fw-bold mb-1" style="font-size: 0.8125rem;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
      Delivery by ${dateFormatted}
    </div>
    <div class="text-muted d-flex flex-wrap gap-2 mt-1" style="font-size: 11.5px;">
      <span>✓ Free Express Delivery</span>
      <span>•</span>
      <span>✓ Cash on Delivery (COD) Available</span>
      <span>•</span>
      <span>✓ 7-Day Easy Return / Exchange</span>
    </div>
  `;

  if (btn) btn.innerText = 'Check';
};

/* ============================================================
   5. Zena All Deals & Offers Modal
   ============================================================ */
window.openDealsModal = function() {
  let modal = document.getElementById('zena-deals-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'zena-deals-modal';
    modal.innerHTML = `
      <div class="zena-deals-backdrop" onclick="closeDealsModal()"></div>
      <div class="zena-deals-modal-dialog">
        <div class="zena-deals-modal-content p-4">
          <!-- Modal Header -->
          <div class="d-flex align-items-center justify-content-between pb-3 mb-3 border-bottom">
            <div class="d-flex align-items-center gap-2">
              <div class="deals-modal-badge-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="19" y1="5" x2="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></svg>
              </div>
              <h5 class="mb-0 fw-bold deals-modal-heading">Available Offers &amp; Coupons</h5>
            </div>
            <button type="button" class="btn-close deals-modal-close" aria-label="Close" onclick="closeDealsModal()"></button>
          </div>
          
          <!-- Deals List -->
          <div class="deals-modal-list d-flex flex-column gap-3">
            
            <!-- Deal 1: PREPAID10 -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag deal-ribbon-gold">
                PREPAID SPECIAL
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    Extra 10% OFF
                  </div>
                  <span class="badge bg-warning-subtle text-warning-emphasis px-2 py-1 fs-xs fw-semibold">Instant Savings</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Instant 10% discount on all UPI, Net Banking &amp; Card prepaid orders.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('PREPAID10', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">PREPAID10</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deal 2: FIRST100 -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag deal-ribbon-purple">
                FIRST ORDER
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    Flat ₹100 OFF
                  </div>
                  <span class="badge bg-primary-subtle text-primary px-2 py-1 fs-xs fw-semibold">Min Spend ₹999</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Flat ₹100 OFF welcome discount on your first order.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('FIRST100', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">FIRST100</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deal 3: ZENA5 (5% OFF on ₹1,499+) -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag deal-ribbon-teal">
                ORDER SPECIAL
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    5% OFF
                  </div>
                  <span class="badge bg-success-subtle text-success px-2 py-1 fs-xs fw-semibold">Min Order ₹1,499</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Get 5% OFF on all purchases of ₹1,499 or above.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('ZENA5', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">ZENA5</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deal 4: ZENA10 (Flat 10% OFF on ₹2,499+) -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag deal-ribbon-teal">
                GRAND SAVINGS
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    Flat 10% OFF
                  </div>
                  <span class="badge bg-success-subtle text-success px-2 py-1 fs-xs fw-semibold">Min Order ₹2,499</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Get Flat 10% OFF on all shopping above ₹2,499.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('ZENA10', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">ZENA10</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deal 5: ZENA500 -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag">
                LUXURY CART
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    Flat ₹500 OFF
                  </div>
                  <span class="badge bg-danger-subtle text-danger px-2 py-1 fs-xs fw-semibold">Min Cart ₹3,499</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Flat ₹500 OFF on luxury shopping of ₹3,499 or above.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('ZENA500', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">ZENA500</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deal 6: LOYALTY15 -->
            <div class="deal-item-card deal-modal-item-card bg-white position-relative">
              <div class="deal-ribbon-tag deal-ribbon-purple">
                VIP LOYALTY
              </div>
              <div class="deal-content-body mt-2">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div class="deal-headline fw-bold">
                    15% OFF
                  </div>
                  <span class="badge bg-secondary-subtle text-secondary px-2 py-1 fs-xs fw-semibold">Repeat VIPs</span>
                </div>
                <div class="deal-subtext text-muted mb-3">
                  Exclusive 15% OFF for repeat VIP clients and 2nd purchase.
                </div>
                <div class="deal-coupon-pill d-flex align-items-center justify-content-between px-3 py-2" onclick="copyCouponCode('LOYALTY15', this); setTimeout(closeDealsModal, 400);">
                  <span class="coupon-code fw-bold">LOYALTY15</span>
                  <div class="d-flex align-items-center gap-1 text-success fw-bold" style="font-size: 11.5px;">
                    <span>TAP TO APPLY</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      <style>
        #zena-deals-modal {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          z-index: 1000000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .zena-deals-backdrop {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(3px);
        }
        .zena-deals-modal-dialog {
          position: relative;
          z-index: 2;
          width: 92%;
          max-width: 480px;
          background: #FAF8F5;
          border: 1px solid rgba(36, 27, 22, 0.1);
          border-radius: 16px;
          box-shadow: 0 20px 48px rgba(0,0,0,0.22);
          max-height: 88vh;
          overflow-y: auto;
          animation: dealsModalUp 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes dealsModalUp {
          from { transform: translateY(20px) scale(0.97); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        .deals-modal-badge-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #59151B;
          color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .deals-modal-heading {
          font-size: 1.05rem;
          color: #1A1A1A;
          letter-spacing: 0.01em;
        }
        .deal-modal-item-card {
          border: 1px solid #ECE5DB !important;
          border-radius: 10px !important;
          padding: 1.45rem 1.15rem 1rem 1.15rem !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03) !important;
          min-width: 100% !important;
          max-width: 100% !important;
        }
        .deal-ribbon-teal {
          background: #108474 !important;
        }
      </style>
    `;
    document.body.appendChild(modal);
  } else {
    modal.style.display = 'flex';
  }
};

window.closeDealsModal = function() {
  const modal = document.getElementById('zena-deals-modal');
  if (modal) modal.style.display = 'none';
};

/* ============================================================
   House of ZENA Recommendation Tab Switcher
   ============================================================ */
window.switchRecTab = function(btn, tabId) {
  document.querySelectorAll('.zena-rec-tab-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');

  document.querySelectorAll('.zena-rec-tab-pane').forEach(pane => {
    pane.classList.add('d-none');
    pane.classList.remove('active');
  });

  const activePane = document.getElementById('tab-' + tabId);
  if (activePane) {
    activePane.classList.remove('d-none');
    activePane.classList.add('active');
  }
};

/* ============================================================
   6. Global Interactive Toast Notification System
   ============================================================ */
window.showZenaToast = function(title, message, iconType = 'success') {
  // XSS sanitization for dynamic content
  const _esc = (s) => String(s || '').replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'})[m]);
  title = _esc(title);
  message = _esc(message);
  let toastContainer = document.querySelector('.zena-toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'zena-toast-container';
    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement('div');
  toast.className = 'zena-toast shadow-lg';
  
  let iconSvg = `
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22C55E" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 6L9 17l-5-5"></path>
    </svg>
  `;
  if (iconType === 'info') {
    iconSvg = `
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    `;
  }

  toast.innerHTML = `
    <div class="zena-toast-icon">${iconSvg}</div>
    <div class="zena-toast-body">
      <h6 class="zena-toast-title mb-0">${title}</h6>
      <p class="zena-toast-message mb-0 text-muted">${message}</p>
    </div>
  `;

  toastContainer.appendChild(toast);
  setTimeout(() => { toast.classList.add('show'); }, 50);

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => { toast.remove(); }, 350);
  }, 4000);
};

/* ============================================================
   7. Contact Form Handler with Instant Feedback
   ============================================================ */
window.handleContactSubmit = function(form) {
  if (!form) return;
  const nameInput = form.querySelector('[name="name"]');
  const name = nameInput ? nameInput.value.trim() : 'Valued Customer';
  
  const submitBtn = form.querySelector('button[type="submit"]');
  if (submitBtn) {
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = 'Sending Message...';

    setTimeout(() => {
      submitBtn.disabled = false;
      submitBtn.innerHTML = 'Message Sent Successfully!';
      submitBtn.classList.remove('btn-danger');
      submitBtn.classList.add('btn-success');

      window.showZenaToast(
        'Inquiry Received',
        `Thank you, ${name}! Our concierge team will respond via email/WhatsApp within 24 hours.`
      );

      form.reset();

      setTimeout(() => {
        submitBtn.innerHTML = originalText;
        submitBtn.classList.remove('btn-success');
        submitBtn.classList.add('btn-danger');
      }, 4000);
    }, 600);
  }
};

/* ============================================================
   8. Track Order Search Handler with Dynamic Output
   ============================================================ */
// Note: Complete reactive controller with dual-mode tabs and milestone feeds
// is embedded in sections/page-track-order.liquid. The global method below
// provides backward compatibility.
if (!window.handleTrackOrder) {
  window.handleTrackOrder = function(form) {
    if (typeof window.quickTrackOrder === 'function') {
      const idInput = form?.querySelector('#track-order-id');
      const contactInput = form?.querySelector('#track-contact');
      window.quickTrackOrder(idInput?.value || 'ZENA-84920', contactInput?.value || '');
    }
  };
}

/* ============================================================
   9. FAQ Category Filtering
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.faq-filter-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const cat = btn.getAttribute('data-category');

      document.querySelectorAll('.faq-filter-btn').forEach(b => {
        b.classList.remove('btn-dark', 'active');
        b.classList.add('btn-outline-dark');
      });
      btn.classList.remove('btn-outline-dark');
      btn.classList.add('btn-dark', 'active');

      document.querySelectorAll('.zena-faq-accordion .accordion-item').forEach(item => {
        const itemCat = item.getAttribute('data-category');
        if (cat === 'all' || itemCat === cat) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
    });
  });

  // Newsletter form submission handling
  document.querySelectorAll('.middle-bar-newsletter-form, .newsletter-form').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const email = input ? input.value.trim() : '';
      if (email) {
        window.showZenaToast('Welcome to The ZENA Circle', `Thank you for subscribing! Your 15% VIP welcome code has been activated.`);
        form.reset();
      }
    });
  });
});

  /* ============================================================
     10. Dynamic Inactive Tab Title Alternator ("✨ Made for you" / "❤️ Looks stunning!")
     ============================================================ */
  (function initTabTitleSwitcher() {
    let savedActiveTitle = document.title;
    let tabSwitchInterval = null;
    const awayTitles = ['✨ Made for you', '❤️ Looks stunning!'];
    let awayIndex = 0;

    function handleVisibilityChange() {
      if (document.hidden) {
        if (!document.title.includes('✨') && !document.title.includes('❤️')) {
          savedActiveTitle = document.title;
        }

        document.title = awayTitles[0];
        awayIndex = 1;

        clearInterval(tabSwitchInterval);
        tabSwitchInterval = setInterval(function() {
          if (!document.hidden) {
            clearInterval(tabSwitchInterval);
            tabSwitchInterval = null;
            if (savedActiveTitle) document.title = savedActiveTitle;
            return;
          }
          document.title = awayTitles[awayIndex];
          awayIndex = (awayIndex + 1) % awayTitles.length;
        }, 3500);

      } else {
        clearInterval(tabSwitchInterval);
        tabSwitchInterval = null;
        if (savedActiveTitle) {
          document.title = savedActiveTitle;
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange, false);
    window.addEventListener('blur', function() {
      if (document.hidden) handleVisibilityChange();
    }, false);
    /* ------------------------------------------------------------
       Footer Mobile Accordions (Zena Spec)
       ------------------------------------------------------------ */
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.footer-accordion-btn');
      if (!btn) return;
      const item = btn.closest('.footer-accordion-item');
      if (!item) return;
      
      const isOpen = item.classList.contains('is-open');
      item.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    });

    /* ------------------------------------------------------------
       House of ZENA — Sticky Catalog Toolbar & Sort/Filter System
       ------------------------------------------------------------ */
    const initZenaCatalogToolbar = function() {
      const sortOffcanvasEl = document.getElementById('offcanvas-collection-sort');
      const sortTriggerLabel = document.getElementById('zena-current-sort-label');

      if (sortOffcanvasEl) {
        sortOffcanvasEl.addEventListener('click', function(e) {
          const sortItem = e.target.closest('.zena-sort-item');
          if (!sortItem) return;
          
          const sortVal = sortItem.getAttribute('data-sort-val');
          const sortText = sortItem.getAttribute('data-sort-text') || sortItem.querySelector('.zena-sort-item-label')?.textContent?.trim();
          if (!sortVal) return;

          // 1. Update active styling in Sort modal
          sortOffcanvasEl.querySelectorAll('.zena-sort-item').forEach(function(item) {
            item.classList.remove('is-active');
            item.setAttribute('aria-checked', 'false');
            item.querySelector('.zena-sort-check-icon')?.remove();
          });

          sortItem.classList.add('is-active');
          sortItem.setAttribute('aria-checked', 'true');
          const btn = sortItem.querySelector('.zena-sort-btn');
          if (btn && !btn.querySelector('.zena-sort-check-icon')) {
            const checkSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            checkSvg.setAttribute('class', 'zena-sort-check-icon');
            checkSvg.setAttribute('width', '16');
            checkSvg.setAttribute('height', '16');
            checkSvg.setAttribute('viewBox', '0 0 24 24');
            checkSvg.setAttribute('fill', 'none');
            checkSvg.setAttribute('stroke', 'currentColor');
            checkSvg.setAttribute('stroke-width', '2.2');
            checkSvg.setAttribute('stroke-linecap', 'round');
            checkSvg.setAttribute('stroke-linejoin', 'round');
            checkSvg.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
            btn.appendChild(checkSvg);
          }

          // 2. Update bottom bar trigger label
          if (sortTriggerLabel && sortText) {
            sortTriggerLabel.textContent = sortText;
          }

          // 3. Hide bottom sheet
          if (window.bootstrap && window.bootstrap.Offcanvas) {
            const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(sortOffcanvasEl) || new window.bootstrap.Offcanvas(sortOffcanvasEl);
            bsOffcanvas.hide();
          } else {
            sortOffcanvasEl.classList.remove('show');
            document.querySelector('.offcanvas-backdrop')?.remove();
          }

          // 4. Update hidden input on any collection-filters-form
          const filterForms = document.querySelectorAll('.collection-filters-form');
          filterForms.forEach(function(form) {
            const sortInput = form.querySelector('input[name="sort_by"]');
            if (sortInput) sortInput.value = sortVal;
          });

          // 5. Update URL and trigger fetch
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('sort_by', sortVal);
          currentUrl.searchParams.delete('page');
          window.history.replaceState({}, '', currentUrl.toString());

          if (typeof window.loadCollection === 'function') {
            window.loadCollection();
          } else {
            window.location.href = currentUrl.toString();
          }
        });
      }

      // Synchronize sort label and filter count on dynamic load
      window.addEventListener('kt.collection.loaded', function() {
        const url = new URL(window.location.href);
        const sortVal = url.searchParams.get('sort_by') || 'best-selling';
        const activeSortItem = sortOffcanvasEl?.querySelector('.zena-sort-item[data-sort-val="' + sortVal + '"]');
        if (activeSortItem && sortTriggerLabel) {
          const text = activeSortItem.getAttribute('data-sort-text') || activeSortItem.querySelector('.zena-sort-item-label')?.textContent?.trim();
          if (text) sortTriggerLabel.textContent = text;
        }
      });
    };

    initZenaCatalogToolbar();
  })();

  /* =========================================  /* ============================================================
     11. GoKwik / KwikPass Phone Auth & Customer Account Controller
     ============================================================ */
  window.openOffcanvasWishlist = function() {
    const el = document.getElementById('offcanvas-wishlist');
    if (el && window.bootstrap && window.bootstrap.Offcanvas) {
      const bsOff = window.bootstrap.Offcanvas.getOrCreateInstance(el);
      bsOff.show();
    }
  };

  window.handleZenaLogout = function() {
    try {
      // Clear cookies
      const cookiesToClear = ['kp_sso_token', 'kp_user_id', 'gokwik_auth', 'kp_auth', 'kp_customer_token', 'logged_in_customer_id', 'zena_logged_in_vip'];
      cookiesToClear.forEach(name => {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        document.cookie = `${name}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      });

      // Clear storage
      ['kp_token', 'kp_user_id', 'kp_sso_token', 'kp_customer', 'gokwik_user_token', 'zena_logged_in_vip', 'zena_user_phone'].forEach(k => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });

      window.isKwikpassLoggedIn = false;

      if (typeof window.showZenaToast === 'function') {
        window.showZenaToast('Signed Out', 'You have been successfully signed out.');
      }

      setTimeout(() => {
        window.location.href = '/account/logout';
      }, 400);
    } catch (err) {
      window.location.href = '/account/logout';
    }
  };

  // Remove any lingering custom account dropdown elements
  try {
    const existingDropdown = document.getElementById('zena-custom-account-dropdown');
    if (existingDropdown) existingDropdown.remove();
  } catch (e) {}

  // Master State Checker
  window.isZenaUserLoggedIn = function() {
    try {
      const cookies = document.cookie || '';
      const hasKpCookie = cookies.includes('kp_sso_token') || 
                            cookies.includes('kp_user_id') || 
                            cookies.includes('gokwik_auth') || 
                            cookies.includes('kp_auth') || 
                            cookies.includes('kp_customer_token') ||
                            cookies.includes('logged_in_customer_id');
      const hasKpStorage = !!(localStorage.getItem('kp_token') || 
                              localStorage.getItem('kp_user_id') || 
                              localStorage.getItem('kp_sso_token') || 
                              localStorage.getItem('kp_customer') || 
                              localStorage.getItem('gokwik_user_token') ||
                              localStorage.getItem('zena_logged_in_vip') === 'true' ||
                              sessionStorage.getItem('kp_token') ||
                              sessionStorage.getItem('kp_sso_token'));
      const hasKpGlobal = window.isKwikpassLoggedIn === true || 
                          (window.kpUser && typeof window.kpUser === 'object' && Object.keys(window.kpUser).length > 0) ||
                          (window.customer && window.customer.id) ||
                          (window.Shopify && window.Shopify.customer && window.Shopify.customer.id);
      return hasKpCookie || hasKpStorage || hasKpGlobal;
    } catch (err) {
      return false;
    }
  };

  // One shared list, used by both close and open. They previously used two
  // different lists: close hid ~33 selectors, open only un-hid 18 of them, so
  // anything in the difference stayed hidden with `!important` inline styles
  // for good and the modal could never be shown again.
  //
  // `.gokwik-container` / `#gokwik-container` are deliberately NOT listed. That
  // is the SDK's own root node and disabling it permanently also takes out
  // GoKwik checkout, not just the login modal.
  window.ZENA_KP_SELECTORS = [
    '#iframe-kp',
    '#kp-iframe',
    '.iframe-kp',
    'iframe[src*="kwikpass"]',
    'iframe[id*="kwikpass"]',
    'iframe[id*="kp-"]',
    '.kp-backdrop',
    '#kp-backdrop',
    '#kwikpass-drawer-desktop',
    '#kwikpass-drawer-mobile',
    '.kp-drawer',
    '[id*="kwikpass-drawer"]',
    '[id*="kp-modal"]',
    '[id*="kwikpass-modal"]',
    '[class*="kp-modal"]',
    '[class*="kwikpass-modal"]',
    '.kp-overlay',
    '#kp-overlay'
  ];

  // The properties close() overrides, so open() can remove exactly those again.
  window.ZENA_KP_HIDDEN_PROPS = ['display', 'visibility', 'opacity', 'pointer-events', 'z-index'];

  window.isZenaKwikpassOpen = function () {
    try {
      const nodes = document.querySelectorAll(window.ZENA_KP_SELECTORS.join(','));
      for (let i = 0; i < nodes.length; i++) {
        const st = window.getComputedStyle(nodes[i]);
        if (st.display !== 'none' && st.visibility !== 'hidden' && st.opacity !== '0') return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  // Master Close / Dismiss Handler for GoKwik & KwikPass Modals & Overlays
  window.closeZenaKwikpass = function() {
    try {
      if (window.__zenaKpObserver) {
        window.__zenaKpObserver.disconnect();
        window.__zenaKpObserver = null;
      }
      const selectors = window.ZENA_KP_SELECTORS;

      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.classList.add('hidden', 'd-none');
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
          el.style.setProperty('opacity', '0', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.style.setProperty('z-index', '-9999', 'important');
        });
      });

      // Remove the old floating "Back to House of Zena" bar if a cached page
      // still has one in the DOM. KwikPass's own X is the only close control.
      const staleBar = document.getElementById('zena-kp-floating-close');
      if (staleBar) staleBar.remove();

      // Clear any lingering backdrops
      document.querySelectorAll('.offcanvas-backdrop, .modal-backdrop, .kp-backdrop').forEach(bd => {
        bd.remove();
      });

      // Restore body and document scroll lock.
      //
      // Repeated on the next frame and again shortly after, because the
      // KwikPass SDK re-applies `overflow: hidden` to the body as part of its
      // own teardown. Clearing it only once left the shopper on a page they
      // could no longer scroll after dismissing the modal.
      const releaseScroll = function () {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.removeProperty('padding-right');
        document.documentElement.style.overflow = '';
      };
      releaseScroll();
      requestAnimationFrame(releaseScroll);
      setTimeout(releaseScroll, 150);
      setTimeout(releaseScroll, 500);

      // Cross-domain postMessage notifications to Kwikpass & parent
      const postMsgs = [
        { event: 'close_login_modal' },
        { type: 'close_iframe' },
        { action: 'close_modal' },
        { type: 'close' },
        { action: 'close' },
        { event: 'close' },
        'close_login_modal',
        'close_iframe',
        'close'
      ];
      // Deliberately NOT posted to our own window. The message listener below
      // treats these same payloads as close signals, so posting them here made
      // close() re-trigger itself in a loop every time the modal was dismissed.

      document.querySelectorAll('iframe[src*="kwikpass"], #iframe-kp, #kp-iframe').forEach(iframe => {
        try {
          if (iframe.contentWindow) {
            postMsgs.forEach(msg => {
              try { iframe.contentWindow.postMessage(msg, '*'); } catch (e) {}
            });
          }
        } catch (e) {}
      });

      // Call any native SDK methods if registered
      if (typeof window.closeIframe === 'function') {
        try { window.closeIframe(); } catch (e) {}
      }
      if (typeof window.kpCloseModal === 'function') {
        try { window.kpCloseModal(); } catch (e) {}
      }
      if (typeof window.handleKpClose === 'function') {
        try { window.handleKpClose(); } catch (e) {}
      }
    } catch (err) {
      console.warn('Zena Kwikpass close handler:', err);
    }
  };

  // The floating "Back to House of Zena" bar has been removed: it meant the
  // shopper had to press two separate close buttons (this one and KwikPass's
  // own X) to dismiss the login modal. KwikPass's X is now the single control.
  // Kept as a no-op so any cached page or third-party call that still invokes
  // it does not throw, and so a stale bar gets cleared.
  window.showZenaKpFloatingClose = function () {
    const staleBar = document.getElementById('zena-kp-floating-close');
    if (staleBar) staleBar.remove();
  };

  // Master Click Handler — Opens GoKwik / KwikPass Phone Login and Account Modal Directly
  window.handleZenaAccountClick = function(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }

    // 1. Immediately dismiss any open offcanvas menu drawers and clear backdrops
    ['offcanvas-menu', 'offcanvas-cart', 'offcanvas-wishlist'].forEach(id => {
      const el = document.getElementById(id);
      if (el && window.bootstrap && window.bootstrap.Offcanvas) {
        try {
          const inst = window.bootstrap.Offcanvas.getInstance(el) || window.bootstrap.Offcanvas.getOrCreateInstance(el);
          if (inst) inst.hide();
        } catch (err) {}
      }
    });
    document.querySelectorAll('.offcanvas-backdrop').forEach(bd => {
      bd.remove();
    });

    // 2. Cleanse and unhide all Kwikpass / GoKwik elements
    const kpSelectors = window.ZENA_KP_SELECTORS;
    document.querySelectorAll(kpSelectors.join(',')).forEach(el => {
      el.classList.remove('hidden', 'd-none');
      window.ZENA_KP_HIDDEN_PROPS.forEach(prop => el.style.removeProperty(prop));
    });

    // 3. Unhide and elevate #iframe-kp
    const iframe = document.getElementById('iframe-kp');
    if (iframe) {
      iframe.classList.remove('hidden', 'd-none');
      iframe.style.setProperty('display', 'block', 'important');
      iframe.style.setProperty('visibility', 'visible', 'important');
      iframe.style.setProperty('opacity', '1', 'important');
      iframe.style.setProperty('pointer-events', 'auto', 'important');
      iframe.style.setProperty('z-index', '2147483647', 'important');
      iframe.style.setProperty('position', 'fixed', 'important');

      // Hand control back to KwikPass.
      //
      // The overrides above are !important, so when the shopper pressed
      // KwikPass's own X its SDK re-added the `hidden` class but our inline
      // rule still won and the modal stayed on screen. That is why a second,
      // floating close button was needed. Watch for the SDK's own hide and
      // run our teardown, so one press of KwikPass's X is enough.
      if (window.__zenaKpObserver) window.__zenaKpObserver.disconnect();
      window.__zenaKpObserver = new MutationObserver(function () {
        const wantsHide = iframe.classList.contains('hidden') ||
          iframe.classList.contains('d-none') ||
          iframe.getAttribute('aria-hidden') === 'true';
        if (wantsHide) {
          window.__zenaKpObserver.disconnect();
          window.__zenaKpObserver = null;
          window.closeZenaKwikpass();
        }
      });
      window.__zenaKpObserver.observe(iframe, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    // 5. Fire SDK modal events and invoke native handlers
    window.dispatchEvent(new CustomEvent('open_login_modal', { 
      detail: { isKpAndShopifyLogin: true, redirectLink: '/account' } 
    }));

    if (typeof window.handleKpAndShopifyLogin === 'function') {
      try { window.handleKpAndShopifyLogin('/account'); } catch (err) {}
    }
    if (typeof window.kpHandleLogin === 'function') {
      try { window.kpHandleLogin('/account'); } catch (err) {}
    }
    if (typeof window.openIframe === 'function') {
      try { window.openIframe('kp_sso_token'); } catch (err) {}
    }

    return false;
  };

  // Keyboard Escape Key listener to dismiss KwikPass
  document.addEventListener('keydown', function(e) {
    // Guarded on the modal actually being open. Unguarded, every Escape press
    // (closing the cart drawer, dismissing a dropdown) stamped the hide styles
    // onto the KwikPass nodes and broke the next open.
    if ((e.key === 'Escape' || e.keyCode === 27) && window.isZenaKwikpassOpen()) {
      window.closeZenaKwikpass();
    }
  });

  // Listen for Browser Back Button (popstate) to Dismiss KwikPass Cleanly
  window.addEventListener('popstate', function() {
    if (window.isZenaKwikpassOpen()) window.closeZenaKwikpass();
  });

  // Universal Cross-Domain Message Listener for KwikPass / GoKwik Events
  window.addEventListener('message', function(event) {
    try {
      let data = event.data;
      if (!data) return;

      // Only the checkout/login providers and our own page may drive this.
      const origin = event.origin || '';
      const trusted = origin === window.location.origin ||
        /(^https?:\/\/([a-z0-9-]+\.)*(gokwik\.co|gokwik\.io|kwikpass\.[a-z.]+|pickrr\.com|shiprocket\.in)$)/i.test(origin);
      if (!trusted) return;

      let rawStr = '';
      if (typeof data === 'string') {
        rawStr = data.toLowerCase();
        try { data = JSON.parse(data); } catch (e) {}
      } else if (typeof data === 'object' && data !== null) {
        try { rawStr = JSON.stringify(data).toLowerCase(); } catch (e) {}
      }

      // Close-signal detection.
      //
      // This used to substring-match the whole stringified payload for things
      // like 'close' and 'dismiss'. Every unrelated analytics message from
      // GoKwik, Shiprocket or Judge.me that happened to contain those
      // letters slammed the modal shut mid-typing, which is what made it feel
      // like it "closes by itself". Matching is now on explicit fields only.
      const CLOSE_VALUES = ['close', 'close_modal', 'close_iframe', 'close_login_modal', 'kp_close', 'gokwik:close', 'gokwik_close', 'dismiss', 'closed'];
      const isCloseValue = function (v) {
        return typeof v === 'string' && CLOSE_VALUES.indexOf(v.toLowerCase()) !== -1;
      };

      let isCloseSignal = isCloseValue(typeof event.data === 'string' ? event.data : null);

      if (!isCloseSignal && typeof data === 'object' && data !== null) {
        isCloseSignal =
          isCloseValue(data.event) ||
          isCloseValue(data.type) ||
          isCloseValue(data.action) ||
          isCloseValue(data.status) ||
          (data.data && typeof data.data === 'object' && (
            isCloseValue(data.data.event) ||
            isCloseValue(data.data.type) ||
            isCloseValue(data.data.action) ||
            isCloseValue(data.data.status)
          ));
      }

      if (isCloseSignal && !rawStr.includes('login_success') && !rawStr.includes('kp_login_success')) {
        window.closeZenaKwikpass();
        return;
      }

      const isLoginSuccess = typeof data === 'object' && data !== null && (
        data.event === 'kp_login_success' || 
        data.action === 'login_success' || 
        data.type === 'KP_LOGIN_SUCCESS' || 
        data.type === 'login_successful' ||
        data.kp_sso_token || 
        (data.data && (data.data.sso_token || data.data.token || data.data.phone))
      );

      if (isLoginSuccess) {
        localStorage.setItem('zena_logged_in_vip', 'true');
        const phone = (typeof data === 'object' && data !== null && (data.phone || (data.data && data.data.phone))) || '';
        if (phone) {
          localStorage.setItem('zena_user_phone', phone);
        }
        window.isKwikpassLoggedIn = true;

        if (typeof window.showZenaToast === 'function') {
          window.showZenaToast('Welcome to ZENA VIP', 'Successfully signed in with phone number.');
        }

        // Smoothly reveal the Account menu with all next steps
        setTimeout(() => {
          const btn = document.querySelector('.zena-kwikpass-account-btn');
          if (btn) window.handleZenaAccountClick({ currentTarget: btn });
        }, 400);
      }
    } catch (err) {}
  });

  // Automatically dismiss Kwikpass whenever ANY offcanvas menu or modal opens (except when account button was clicked)
  ['show.bs.offcanvas', 'show.bs.modal'].forEach(evtName => {
    document.addEventListener(evtName, function() {
      // The old guard tested `e.target.closest('.zena-kwikpass-account-btn')`,
      // but e.target is the offcanvas element and never contains that button -
      // so this ran on every single drawer open and permanently disabled the
      // KwikPass nodes, which is why the account icon stopped responding after
      // the menu or cart had been opened once.
      if (window.isZenaKwikpassOpen()) {
        window.closeZenaKwikpass();
      }
    });
  });

  // The MutationObserver that used to sit here existed only to show and hide
  // the floating close bar. With that bar gone it had no purpose, and it was
  // watching every attribute change on the whole body subtree.

  (function () {
    // NOTE: this block used to end with `}, false);` instead of `})();`, which
    // made the whole thing parse as the sequence expression
    // `(function () { ... }, false);`. The function was therefore never
    // invoked, so none of the listeners below were ever registered: the
    // KwikPass account button had no handler, the GoKwik trigger was dead, and
    // the Shiprocket Buy Now button on product pages did nothing.

    // Capture phase, so header/menu clicks are seen before any third-party
    // handler can stop propagation.
    document.addEventListener('click', function (e) {
      const isAccountBtn = e.target.closest('.zena-kwikpass-account-btn');

      if (isAccountBtn) {
        window.handleZenaAccountClick(e);
        return;
      }

      const menuToggle = e.target.closest('[href="#offcanvas-menu"], [data-bs-target="#offcanvas-menu"], [aria-controls="offcanvas-menu"], .navbar-mobile-left a');
      const anyOffcanvasToggle = e.target.closest('[data-bs-toggle="offcanvas"], [href^="#offcanvas-"], [data-bs-target^="#offcanvas-"]');
      const headerAction = e.target.closest('#header-group, #navbar-wrapper, .navbar, .navbar-mobile-row, .navbar-top-row');

      if (window.isZenaKwikpassOpen && window.isZenaKwikpassOpen() && (menuToggle || anyOffcanvasToggle || headerAction)) {
        window.closeZenaKwikpass();

        if (menuToggle) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(function () {
            const menuEl = document.getElementById('offcanvas-menu');
            if (menuEl && window.bootstrap && window.bootstrap.Offcanvas) {
              window.bootstrap.Offcanvas.getOrCreateInstance(menuEl).show();
            }
          }, 60);
          return;
        }
      }

      // Close the account dropdown on an outside click.
      const customDropdown = document.getElementById('zena-custom-account-dropdown');
      if (customDropdown && customDropdown.style.display !== 'none') {
        if (!e.target.closest('#zena-custom-account-dropdown')) {
          customDropdown.style.display = 'none';
        }
      }
    }, true);

    // GoKwik 1-Click Fast Checkout & Buy Now Handlers
    document.addEventListener('click', function (e) {
      const checkoutBtn = e.target.closest('.gokwik-checkout, .btn-checkout, [data-gokwik-checkout], [data-checkout]');
      if (checkoutBtn) {
        if (typeof window.handleGoKwikCheckout === 'function') {
          e.preventDefault();
          window.handleGoKwikCheckout(checkoutBtn);
          return;
        }
      }

      const buyNowBtn = e.target.closest('.btn-buy, .sticky-btn-buy, [data-gokwik-buy]');
      if (buyNowBtn) {
        if (typeof window.handleGoKwikBuyNow === 'function') {
          e.preventDefault();
          window.handleGoKwikBuyNow(buyNowBtn);
          return;
        }
        const form = buyNowBtn.closest('form') || document.querySelector('.product-form, form[action*="/cart/add"]');
        const variantId = form ? (form.querySelector('[name="id"]')?.value) : null;
        const qty = form ? (parseInt(form.querySelector('[name="quantity"]')?.value) || 1) : 1;
        if (variantId) {
          e.preventDefault();
          buyNowBtn.classList.add('loading');
          window.location.href = `/cart/${variantId}:${qty}`;
          return;
        }
      }
    }, false);
  })();

  /* ============================================================
     HOUSE OF ZENA — SPECULATIVE INSTANT NAVIGATION ENGINE
     Fetches pages on hover/touchstart for sub-second instant loads
     ============================================================ */
  (function() {
    // Skip if user has data-saver enabled or on slow 2G
    if (navigator.connection && (navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType))) {
      return;
    }

    const prefetchedUrls = new Set();
    const currentOrigin = window.location.origin;

    function prefetchUrl(urlStr) {
      try {
        if (!urlStr || prefetchedUrls.has(urlStr)) return;
        const url = new URL(urlStr, currentOrigin);

        // Only prefetch same-origin internal pages
        if (url.origin !== currentOrigin) return;

        const path = url.pathname;
        // Ignore static assets, checkout, cart mutations, admin, or search API
        if (path.startsWith('/cart') || path.startsWith('/checkout') || path.startsWith('/admin') ||
            path.startsWith('/cdn-cgi') || path.endsWith('.json') || path.endsWith('.js') || path.endsWith('.css')) {
          return;
        }

        // Must be a content, collection, product, or policy route
        if (!(path.startsWith('/collections') || path.startsWith('/products') || path.startsWith('/pages') || path.startsWith('/blogs') || path === '/')) {
          return;
        }

        prefetchedUrls.add(urlStr);

        // Use modern prefetch link tag or fetch with low priority
        if (document.createElement('link').relList && document.createElement('link').relList.supports && document.createElement('link').relList.supports('prefetch')) {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = urlStr;
          link.as = 'document';
          document.head.appendChild(link);
        }
        if (typeof fetch === 'function') {
          fetch(urlStr, { priority: 'low', credentials: 'include', cache: 'force-cache' }).catch(function() {});
        }
      } catch (e) {}
    }

    function handleLinkIntent(e) {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('data:')) {
        return;
      }

      prefetchUrl(link.href);
    }

    // handleLinkIntent was defined but never attached, and this IIFE was never
    // closed - which made the whole of custom.js a syntax error, so none of the
    // file ran at all. Wire the listeners up and close the block.
    document.addEventListener('mouseover', handleLinkIntent, { passive: true });
    document.addEventListener('touchstart', handleLinkIntent, { passive: true });
    document.addEventListener('focusin', handleLinkIntent, { passive: true });
  })();

  /* ============================================================
     HOUSE OF ZENA — PERMANENT CHECKOUT BUTTON GUARANTEE WATCHDOG
     Guarantees that the checkout button is ALWAYS present & visible
     in the cart drawer across mobile and desktop.
     ============================================================ */
  (function() {
    function ensureCheckoutButton() {
      const offcanvasCart = document.getElementById('offcanvas-cart');
      if (!offcanvasCart) return;

      const items = offcanvasCart.querySelectorAll('.cart-item');
      const footer = offcanvasCart.querySelector('.offcanvas-footer');
      if (!footer || items.length === 0) return;

      // Check if checkout button exists anywhere in footer
      const existingBtn = footer.querySelector('.zena-luxury-checkout-btn, .gokwik-checkout, button[name="checkout"], .btn-checkout, [data-checkout]');
      if (existingBtn) {
        // Remove any orphan duplicate buttons
        footer.querySelectorAll('.btn-checkout:not(.zena-luxury-checkout-btn):not(.gokwik-checkout)').forEach(b => b.remove());
        return;
      }

      let form = footer.querySelector('form[action*="/cart"], form.shopify-cart-form');
      if (!form) {
        form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart';
        form.className = 'shopify-cart-form';
        footer.insertBefore(form, footer.querySelector('.btn-link') || footer.lastElementChild);
      }

      const btn = document.createElement('button');
      btn.className = 'btn btn-checkout btn-primary btn-lg zena-luxury-checkout-btn gokwik-checkout w-100';
      btn.name = 'checkout';
      btn.type = 'button';
      btn.setAttribute('data-checkout', 'true');
      btn.setAttribute('data-gokwik-checkout', 'true');
      btn.innerHTML = `
        <span class="zena-checkout-btn-content d-flex align-items-center justify-content-between w-100">
          <span class="zena-checkout-left d-flex align-items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-white flex-shrink-0" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span class="zena-checkout-text-block text-start d-flex flex-column" style="line-height: 1.15;">
              <span class="zena-checkout-title text-uppercase fw-bold text-white" style="letter-spacing: 0.06em; font-size: 13.5px;">CHECKOUT</span>
              <span class="zena-checkout-subtitle" style="font-size: 9.5px; font-weight: 500; color: rgba(255, 255, 255, 0.85); letter-spacing: 0.04em;">UPI / CARDS / COD</span>
            </span>
          </span>
          <span class="zena-checkout-right d-flex align-items-center gap-2">
            <span class="zena-checkout-pay-icons d-inline-flex align-items-center">
              <img src="https://cdn.gokwik.co/v4/images/upi-icons.svg" alt="UPI / Cards" height="20" width="80" style="height: 20px; width: auto; display: block;" loading="eager">
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-white flex-shrink-0" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </span>
        </span>
      `;
      btn.onclick = function() {
        if (typeof window.handleGoKwikCheckout === 'function') {
          window.handleGoKwikCheckout(this);
        } else if (window.gokwikSdk && typeof window.gokwikSdk.initCheckout === 'function') {
          window.gokwikSdk.initCheckout(window.merchantInfo);
        }
      };
      form.appendChild(btn);
    }

    // Shiprocket injects its own checkout button asynchronously. Checking
    // immediately would find nothing and add a second, native button next to
    // it, so each trigger re-checks after a short grace period instead.
    var pendingCheck = null;
    function scheduleCheck() {
      if (pendingCheck) clearTimeout(pendingCheck);
      pendingCheck = setTimeout(function() {
        pendingCheck = null;
        ensureCheckoutButton();
      }, 600);
    }

    // Driven by cart mutations and drawer opens rather than a permanent 400ms
    // timer, which ran 2.5x a second for the entire life of every page.
    window.addEventListener('cart:updated', scheduleCheck);
    window.addEventListener('kt.cart.reloaded', scheduleCheck);
    window.addEventListener('kt.cart.added', scheduleCheck);
    window.addEventListener('kt.cart.changed', scheduleCheck);
    document.addEventListener('shown.bs.offcanvas', function(e) {
      if (e.target && e.target.id === 'offcanvas-cart') {
        scheduleCheck();

        // Catch the drawer being re-rendered underneath us while it is open.
        var observer = new MutationObserver(scheduleCheck);
        observer.observe(e.target, { childList: true, subtree: true });
        e.target.addEventListener('hidden.bs.offcanvas', function stop() {
          observer.disconnect();
          e.target.removeEventListener('hidden.bs.offcanvas', stop);
        });
      }
    });
  })();

  /* ============================================================
     23. PDP Floating Gallery Product Share Handler
     ============================================================ */
  window.handleProductShare = function(btn, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    var targetBtn = btn || (event && event.currentTarget) || document.querySelector('.cc-stage-share-btn');
    var title = (targetBtn && (targetBtn.getAttribute('data-share-title') || targetBtn.getAttribute('data-product-title'))) || document.title || 'House of Zena';
    var url = (targetBtn && (targetBtn.getAttribute('data-share-url') || targetBtn.getAttribute('data-product-url'))) || window.location.href;

    function showToast(titleText, msgText) {
      if (typeof window.showZenaToast === 'function') {
        window.showZenaToast(titleText, msgText, 'success');
      }
    }

    function copyFallback(link) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(function() {
          showToast('Link Copied!', 'Share this piece with your friends & family.');
        }).catch(function() {
          legacyCopy(link);
        });
      } else {
        legacyCopy(link);
      }
    }

    function legacyCopy(link) {
      var input = document.createElement('input');
      input.value = link;
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      input.style.top = '0';
      document.body.appendChild(input);
      input.focus();
      input.select();
      try {
        document.execCommand('copy');
        showToast('Link Copied!', 'Share this piece with your friends & family.');
      } catch(e) {}
      document.body.removeChild(input);
    }

    if (navigator.share) {
      navigator.share({
        title: title + ' — House of Zena',
        text: 'Look what I found at House of Zena: ' + title + ' ✨',
        url: url
      }).then(function() {
        showToast('Shared Successfully', 'Thank you for sharing House of Zena jewellery!');
      }).catch(function(err) {
        if (err && err.name !== 'AbortError') {
          copyFallback(url);
        }
      });
    } else {
      copyFallback(url);
    }
  };

  // Global event delegation for reliable mobile & desktop clicks
  try {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('click', function(e) {
        try {
          var shareBtn = e.target && e.target.closest && e.target.closest('.cc-stage-share-btn, .product-media-gallery-share');
          if (shareBtn) {
            window.handleProductShare(shareBtn, e);
          }
        } catch(err) {}
      }, { passive: true });
    }
  } catch(e) {}

  /* ============================================================
     Sold Out Products Bottom Shift Helper
     Ensures all sold-out products in collection grids appear strictly
     at the very bottom (below Load More button & below all available items).
     ============================================================ */
  window.zenaShiftSoldOutToEnd = function(targetContainer) {
    try {
      var soldOutGrid = document.querySelector('#zena-soldout-products-grid') || document.querySelector('.zena-soldout-products-grid');
      var loadMoreBtn = document.querySelector('load-more-products, .load-more-products, .collection-pagination, .zena-explore-categories-cta');

      var containers = [];
      if (targetContainer && targetContainer.nodeType === 1) {
        containers = [targetContainer];
      } else {
        containers = Array.from(document.querySelectorAll(
          '.zena-available-products-grid, .product-list, .zena-bs-grid, .zena-ts-grid, .zena-top-styles-grid, .zena-sbb-grid, .swiper-wrapper'
        ));
      }

      containers.forEach(function(container) {
        if (!container || !container.children || container.children.length === 0) return;
        if (container.id === 'zena-soldout-products-grid' || container.getAttribute('data-grid-type') === 'sold-out') return;

        var isCollectionGrid = container.classList.contains('zena-available-products-grid') || 
                               (container.closest('.collection-products') && !container.classList.contains('zena-soldout-products-grid'));

        var children = Array.from(container.children);
        var soldOuts = [];
        children.forEach(function(child) {
          if (!child || child.nodeType !== 1) return;
          if (child.classList.contains('zena-cross-category-divider') || 
              child.classList.contains('load-more-products') || 
              child.classList.contains('collection-pagination') ||
              child.tagName === 'TEMPLATE') {
            return;
          }

          var isSoldOut = false;
          if (child.classList.contains('is-sold-out') || child.classList.contains('product-card--sold-out')) {
            isSoldOut = true;
          } else if (child.getAttribute('data-available') === 'false') {
            isSoldOut = true;
          } else if (child.querySelector('.btn-sold-out, .product-card--sold-out, [data-available="false"]')) {
            isSoldOut = true;
          } else {
            var btn = child.querySelector('.btn-atc, button[name="add"], .zena-demand-trigger');
            if (btn) {
              var txt = (btn.textContent || '').trim().toLowerCase();
              if (txt.includes('sold out') || btn.classList.contains('btn-sold-out') || btn.classList.contains('zena-demand-trigger')) {
                isSoldOut = true;
              }
            }
          }

          if (isSoldOut) {
            child.classList.add('is-sold-out');
            child.setAttribute('data-available', 'false');
            soldOuts.push(child);
          }
        });

        if (soldOuts.length > 0) {
          if (isCollectionGrid) {
            // Target the dedicated sold-out grid situated BELOW load-more
            var soldOutHeader = document.querySelector('#zena-soldout-header');
            if (!soldOutGrid) {
              soldOutGrid = document.createElement('div');
              soldOutGrid.id = 'zena-soldout-products-grid';
              soldOutGrid.className = container.className;
              soldOutGrid.classList.remove('zena-available-products-grid');
              soldOutGrid.classList.add('zena-soldout-products-grid', 'mt-2');
              soldOutGrid.setAttribute('role', 'list');
              soldOutGrid.setAttribute('data-grid-type', 'sold-out');
              if (loadMoreBtn && loadMoreBtn.parentNode) {
                loadMoreBtn.parentNode.insertBefore(soldOutGrid, loadMoreBtn.nextSibling);
              } else if (container.parentNode) {
                container.parentNode.appendChild(soldOutGrid);
              }
            }
            soldOutGrid.classList.remove('d-none');
            if (soldOutHeader) soldOutHeader.classList.remove('d-none');
            soldOuts.forEach(function(el) {
              soldOutGrid.appendChild(el);
            });
          } else {
            // Non-collection grids (e.g. homepage sliders): keep at end of container
            soldOuts.forEach(function(el) {
              container.appendChild(el);
            });
          }
        }
      });

      var soldOutHeader = document.querySelector('#zena-soldout-header');
      if (soldOutGrid && soldOutGrid.children.length === 0) {
        soldOutGrid.classList.add('d-none');
        if (soldOutHeader) soldOutHeader.classList.add('d-none');
      } else if (soldOutGrid && soldOutGrid.children.length > 0) {
        soldOutGrid.classList.remove('d-none');
        if (soldOutHeader) soldOutHeader.classList.remove('d-none');
      }
    } catch(err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'zenaShiftSoldOutToEnd error', err);
      }
    }
  };

  /* ============================================================
     Product Background Contrast Balancer
     Interleaves/distributes products photographed on dark mannequin
     busts/hands amongst white studio packshots so the visual contrast
     is distributed elegantly rather than grouped in solid blocks.
     ============================================================ */
  window.zenaBalanceBackgrounds = function(targetContainer) {
    try {
      var container = targetContainer || document.querySelector('.zena-available-products-grid');
      if (!container || !container.children || container.children.length === 0) return;
      if (container.id === 'zena-soldout-products-grid' || container.getAttribute('data-grid-type') === 'sold-out') return;

      var darkKeywords = [
        'hand chain', 'hathphool', 'imperial scallop', 'main event', 'art deco baguette', 'heart on my sleeve', 
        'inseparable', 'can you keep a secret', 'sicilian crescent', 'décolletage lariat', 'tribeca paperclip',
        'sunkissed in roma', 'liquid silk herringbone', 'liquid asset', 'midnight aperitivo pavé bangle',
        'caught in orbit', 'plot twist', 'melted butter signet', 'parisienne braid',
        'high tide pavé ripple', 'roman column fluted', 'instant stack', 'butter puff',
        'queen bee honeycomb mosaic', 'modern love solitaire bar', 'good karma only nazar mangalsutra',
        'tied the knot pavé mangalsutra', 'celestial arch layered'
      ];

      var isDarkProduct = function(card) {
        if (!card || card.nodeType !== 1) return false;
        var img = card.querySelector('.product-card-img-primary') || card.querySelector('img');
        var src = (img ? (img.getAttribute('src') || img.getAttribute('data-src') || '') : '').toLowerCase();
        var title = (card.querySelector('.product-card-title, .card-title, h3, h2')?.textContent || '').toLowerCase();

        if (src.includes('placeholder-zena')) return false;
        if (src.includes('zena-2') || src.includes('img_8') || src.includes('hand-chain') || src.includes('mannequin') || src.includes('model-detail')) {
          return true;
        }
        for (var i = 0; i < darkKeywords.length; i++) {
          if (title.includes(darkKeywords[i])) return true;
        }
        return false;
      };

      var children = Array.from(container.children);
      var whiteCards = [];
      var darkCards = [];

      children.forEach(function(child) {
        if (!child || child.nodeType !== 1) return;
        if (child.classList.contains('zena-cross-category-divider') || 
            child.classList.contains('load-more-products') || 
            child.classList.contains('collection-pagination') ||
            child.tagName === 'TEMPLATE') {
          return;
        }
        if (child.classList.contains('is-sold-out') || child.getAttribute('data-available') === 'false') {
          return;
        }

        if (isDarkProduct(child)) {
          child.setAttribute('data-bg-tone', 'dark-contrast');
          darkCards.push(child);
        } else {
          child.setAttribute('data-bg-tone', 'white-studio');
          whiteCards.push(child);
        }
      });

      if (darkCards.length === 0 || whiteCards.length === 0) return;

      var total = whiteCards.length + darkCards.length;
      var result = [];
      var wIdx = 0;
      var dIdx = 0;
      var acc = 0;

      for (var j = 0; j < total; j++) {
        acc += darkCards.length;
        if (acc >= total && dIdx < darkCards.length && j > 0) {
          result.push(darkCards[dIdx++]);
          acc -= total;
        } else if (wIdx < whiteCards.length) {
          result.push(whiteCards[wIdx++]);
        } else if (dIdx < darkCards.length) {
          result.push(darkCards[dIdx++]);
        }
      }

      result.forEach(function(el) {
        container.appendChild(el);
      });
    } catch(err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'zenaBalanceBackgrounds error', err);
      }
    }
  };

  var runZenaCollectionArrangements = function() {
    window.zenaBalanceBackgrounds();
    window.zenaShiftSoldOutToEnd();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runZenaCollectionArrangements);
  } else {
    runZenaCollectionArrangements();
  }
  window.addEventListener('load', runZenaCollectionArrangements);
  window.addEventListener('kt.collection.loaded', runZenaCollectionArrangements);
})();
